import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { isEmpty } from "lodash";
import IconButton from "components/items/icon-button";
import { Hidden, Typography } from "@material-ui/core";

import { setCurrentAttachment, resetCurrentAttachment } from "app/main/patients/actions/patientOCR.selector.actions";
import { getCurrentAttachment, getCurrentReport } from "app/main/patients/reducers/patientOCR.selector.reducers";
import ReferralSourceDocumentPreview from "app/main/referrals/components/add-referral-form/referralSourceDocumentPreview";
import { isCurrentReferralSourceDocumentFileLoading } from "app/main/referrals/reducers/referrals.reducers";

const useStyles = makeStyles(theme => ({
  rootContainer: {
    flexDirection: "row",
    flex: "auto 1",
    height: "100%",
  },
  headerContainer: {
    display: "flex",
    backgroundColor: theme.palette.background.default,
    alignItems: "center",
    marginTop: theme.spacing(1),
    height: 72,
    borderBottom: "1px solid #e0e0e0",
  },
  headerContentContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    margin: theme.spacing(2),
  },
  headerActionContainer: {
    flex: 1,
    display: "flex",
    justifyContent: "flex-end",
    marginRight: theme.spacing(2),
    backgroundColor: "transparent",
  },
  contentContainer: {
    display: "flex",
    height: "calc(100% - 80px)",
    flex: "1 1",
  },
  specialtySelector: {
    minWidth: 320,
    marginRight: "8px",
  },
  leftContainer: {
    overflowX: "hidden",
    margin: theme.spacing(0, 2, 0, 0),
    position: "relative",
    flex: 1,
    height: "100%",
  },
  rightContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderLeft: "1px solid #e0e0e0",
    paddingTop: "8px",
  },
}));

const OCRProcessReferral = ({
  orgUnitId,
  renderContent,
  referredToSpecialty = null,
  onClose,
  onCancel,
  title = null,
  disabledRemove = false,
  disabledAddDocument = false,
}) => {
  const classes = useStyles();
  const ref = useRef();
  const [width, setWidth] = useState(0);
  const dispatch = useDispatch();

  const isReferralSourceDocumentFileLoading = useSelector(isCurrentReferralSourceDocumentFileLoading);
  const currentAttachment = useSelector(getCurrentAttachment);
  const currentReport = useSelector(getCurrentReport);
  const [isReading] = useState(false);
  const [assignToSpecialty, setAssignToSpecialty] = useState(referredToSpecialty);

  const updateWidth = () => {
    setWidth(ref.current?.clientWidth ? ref.current?.clientWidth / 2 : 0);
  };

  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => { window.removeEventListener("resize", updateWidth); };
  }, []);

  useEffect(() => {
    if (referredToSpecialty?.value) {
      setAssignToSpecialty(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referredToSpecialty]);

  const onFileUploaded = uploadedSourceDocumentFile => {
    if (uploadedSourceDocumentFile) {
      dispatch(setCurrentAttachment(uploadedSourceDocumentFile));
    }
  };

  const onFileRemove = () => {
    dispatch(resetCurrentAttachment());
  };

  const fileData = currentAttachment?.fileData
    || currentAttachment?.base64File
    || currentAttachment?.htmlPreview
    || currentReport?.printableViewContent;
  const fileType = currentAttachment?.mimeType || currentReport?.printableViewType;
  const hasPreview = !isEmpty(fileData);
  const showFileRemove = !disabledRemove && !isReading && hasPreview;

  const renderHeader = () => (
    <div className={classes.headerContentContainer}>
      <Typography variant="subtitle1" color="secondary">{title ?? "Add Referral"}</Typography>
      {showFileRemove && (
        <IconButton style={{ marginLeft: "4px" }} title="Remove file" icon="delete" disabled={isReading} edge="end" onClick={onFileRemove} className="pr-8" />
      )}
    </div>
  );

  const onCloseAction = () => {
    onCancel();
    onClose();
  };

  return (
    <div className={classes.rootContainer} elevation={1}>
      <div className={classes.headerContainer}>
        {renderHeader()}
        <div className={classes.headerActionContainer}>
          <IconButton title="Close" onClick={() => onCloseAction()} icon="close" />
        </div>
      </div>
      <div className={classes.contentContainer} ref={ref}>
        <Hidden smDown>
          <div className={classes.leftContainer}>
            <ReferralSourceDocumentPreview
              fileData={fileData}
              mimeType={fileType}
              pdfProps={{ paged: true, showPreviewControls: true, width: width > 960 ? 960 : width }}
              hasPreview
              loading={isReferralSourceDocumentFileLoading}
              orgUnitId={orgUnitId}
              label="Drag & drop your pdf source referral here or click on the box to upload"
              onFileUploaded={onFileUploaded}
              specialtyId={assignToSpecialty?.value}
              disabled={isReading || disabledAddDocument}
            />
          </div>
        </Hidden>
        <div className={classes.rightContainer}>
          {renderContent({ referredToSpecialty: assignToSpecialty, contentClasses: { root: classes.rightContent } })}
        </div>
      </div>
    </div>
  );
};

export default OCRProcessReferral;
